import React, { useMemo, useContext, useEffect } from 'react';
import dynamic from 'next/dynamic';
import ContentPane from '@/components/ContentPane/ContentPane';
import AssessmentPage from '@/components/pages/assessments/AssessmentPage';
import WithStandardLayout from '@/layouts/withStandard.layout';
import WithReferralLayout from '@/layouts/withReferral.layout';
import initializeIconLibrary from '@/utils/fontAwesomeLibrary';
import { languageContext } from '@/context/language.context';
import apiAssessments from '@/utils/api/odas/assessments';
import useAccount from '@/hooks/useAccount';
import useReAuth from '@/hooks/useReAuth';
import t from '@/utils/Language/Assessments-translations';
import { modalContext } from '@/context/modal.context';
import useMixpanel from '@/hooks/useMixpanel';
import useLog from '@/hooks/useLog';
import { useRouter } from 'next/router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ButtonPrimary from '@/components/buttons/ButtonPrimary';
import MessageAlert from '@/components/icons/MessageAlert';
import { AccountContext } from '@/context/account.context';
import { toastContext } from '@/context/toast.context';
import { getEventsAPI } from '@/utils/api/odas/assessments/events';
import { buildServerSideProps } from '@/utils/server';
import { referralContext } from '@/context/referral.context';
import 'bootstrap/dist/css/bootstrap.min.css';

initializeIconLibrary();

const openEditClientModal = (modal, done) => {
  modal.enqueueModal({
    name: 'EditClientModal',
    agencyClient: {},
    orgNetwork: {},
    showSalesforce: false,
    actionType: 'searchClients',
    openConnectToSalesforceModal: false,
    handleSelectedClient: async (client) => {
      done({ close: modal.closeModal }, client);
    }
  });
};

const openConfirmRestartModal = (modal, t, api, assessmentId, agencyClientId, invitation, done) => {
  modal.enqueueModal({
    name: 'ConfirmationModal',
    title: <MessageAlert>{t('Are you sure?')}</MessageAlert>,
    text: t('Are you sure you want to restart this assessment? This will clear your results.'),
    onConfirm: async () => {
      const res = await api.restartAssessment(assessmentId, agencyClientId, invitation);
      done({ close: modal.closeModal, res: res.data }, true);
    },
    onCancel: () => {
      done({ close: modal.closeModal }, false);
    }
  });
};

const openPromptLogoutModal = (modal, t, logout) => {
  modal.enqueueModal(
    {
      name: 'ErrorMessageModal',
      title: t('Please log out'),
      message: t('Looks like you are already logged in. Please log out to complete the assessment.'),
      onButtonClick: () => logout(),
      buttonText: t('Log out')
    },
    { forceOpen: true }
  );
};

const AssessmentsPage = () => {
  const account = useAccount();
  const router = useRouter();
  const { s, client: clientId, all, locale, invitation_id = '', invitation_token = '' } = router.query;
  const { currentLanguage, setLanguage } = useContext(languageContext);
  const slug = all ? all[0] : '';
  const { data: user, isSessionRetrieved } = useContext(AccountContext);
  const { persistReferralClient } = useContext(referralContext);
  const { addToast } = useContext(toastContext);
  const { track } = useMixpanel();
  const { log } = useLog();
  const modal = useContext(modalContext);
  const logging = {
    reqParams: { slug, s, clientId }, // pass along parameters coming in from this request
    events: getEventsAPI({ track, log, slug, params: { s } }),
    alert: {
      errorMessage: (message, opts = {}) => {
        addToast(Object.assign({ type: 'error', delayed: 10000 }, opts || {}, { body: message }));
      }
    }
  };
  const invitation = invitation_id ? { id: invitation_id, token: invitation_token } : {};

  useReAuth();

  useEffect(() => {
    if (locale && locale !== currentLanguage) setLanguage(locale);
  }, [locale]);

  const logout = async () => {
    await account.logout();
    window.location.reload();
  };

  const redirects = {
    error: (msg) => {
      if (msg) addToast({ body: msg });
      router.push('/plan#assessments');
    }
  };

  const api = useMemo(() => apiAssessments.session(user, currentLanguage), [user?.id, currentLanguage]);

  const loading = <p>{t('Loading...')}</p>;
  const ResultsHIVRisk = dynamic(() => import('@/components/Assessments/ResultsHIVRisk', { loading }));
  const ResultsInterface = dynamic(() => import('@/components/Assessments/ResultsInterface', { loading }));

  const components = () => ({
    // shared components we want to make available to the AssessmentPage application
    PrimaryButton: (props = {}) => <ButtonPrimary {...props} />,
    IconChevronRight: () => <FontAwesomeIcon icon={['fas', 'caret-right']} />,

    // ability to override the default results page
    results: ({ slug, props }) => {
      // This is so hacky!
      if (slug.startsWith('ecm-screening') || slug.startsWith('enhanced-care-management')) {
        return <ResultsInterface {...props} />;
      } else if (slug == 'hiv-risk') {
        return <ResultsHIVRisk {...props} />;
      } else {
        return null;
      }
    },

    prompts: {
      modal: {
        choose_client: ({ done }) => openEditClientModal(modal, done),
        confirm_restart_assessment: ({ done, t, assessmentId, agencyClientId }) => {
          openConfirmRestartModal(modal, t, api, assessmentId, agencyClientId, invitation, done);
        },
        require_logout: () => openPromptLogoutModal(modal, t, logout)
      }
    }
  });

  if (!slug) return null;

  return (
    <ContentPane>
      <AssessmentPage
        slug={slug}
        api={api}
        locale={currentLanguage}
        user={user}
        components={components}
        logging={logging}
        isSessionRetrieved={isSessionRetrieved}
        persistReferralClient={persistReferralClient}
        redirects={redirects}
        query={router.query}
      />
    </ContentPane>
  );
};

export const getServerSideProps = buildServerSideProps();

AssessmentsPage.getLayout = function (page) {
  const router = useRouter();
  const { all } = router.query;
  const slug = all ? all[0] : '';
  const project = slug.startsWith('ecm-screening') ? 'interface' : 'onedegree';

  return (
    <WithStandardLayout host={page.props.host}>
      <WithReferralLayout project={project}>{page}</WithReferralLayout>
    </WithStandardLayout>
  );
};

export default AssessmentsPage;
